import * as React from "react";
import { Button, styled } from "@mui/material";
import { PatientPopup } from "../components/uiControllers/patientPopup";
import { useState } from "react";
import { ACTION, CATEGORY, evt } from "fe-common/analytics/analytics";
import { useClinicData } from "../clinic/clinicHooks";
import { StarRating } from "fe-web-components/rating/starRating";
import { GenericButton } from "../components/uiControllers/genericButton";
import { useTranslate } from "../hooks/useTranslate";
import { PatientTextField } from "../components/uiControllers/patientTextField";
import { PatientColorMap } from "./patientColorMap";
import { usePatient } from "../state/patientContext";

const REVIEW_POPUP_DISMISSED_KEY = (caseId) => `${caseId}_FEEDBACK_DISMISSED`;
//region [[ Styles ]]

const ReviewPopupView = styled(PatientPopup)({
  "& .patient-popup-container": {
    padding: "17px",
    paddingTop: "46px",
    margin: "5%",
    left: "auto",
    width: "90%",
    bottom: 0,
  },
  "& .star-rating": {
    width: "275px",
  },
});

const ThanksTitleArea = styled("div")({
  color: PatientColorMap.Primary,
  fontSize: "22px",
  fontWeight: 700,
  lineHeight: "31px",
  display: "flex",
  flexFlow: "row",
  alignItems: "center",
  justifyContent: "flex-start",
  marginBottom: "9px",
  textAlign: "center",
  width: "235px",
});

const SubtitleArea = styled("div")({
  color: PatientColorMap.Default,
  fontSize: "15px",
  fontWeight: 500,
  marginBottom: "12px",
});

const TextAndButtonArea = styled("div")({
  borderRadius: "30px",
  background: PatientColorMap.DefaultLight,
  padding: "18px 25px",
  display: "flex",
  flexFlow: "column",
  alignItems: "center",
  justifyContent: "center",
  width: "100%",
  "& > *": {
    width: "100%",
  },
  "& fieldset": {
    background: PatientColorMap.White,
    zIndex: 0,
  },
  "& textarea": {
    zIndex: 1,
  },
  marginTop: "23px",
  marginBottom: "15px",
});

const FeedbackDescription = styled("div")({
  fontWeight: 700,
  fontSize: "15px",
  lineHeight: "19px",
  textAlign: "center",
  marginBottom: "11px",
});

const DismissButton = styled(Button)({
  marginTom: "29px",
  "&:hover": {
    background: "none",
  },
  "& > span": {
    textTransform: "none",
  },
});

//endregion [[ Styles ]]

//region [[ Props ]]

export interface ReviewPopupProps {}

//endregion [[ Props ]]

//region [[ Functions ]]
export const wasReviewPopupDismissed = (caseId: string) => {
  const sessionValue = sessionStorage.getItem(
    REVIEW_POPUP_DISMISSED_KEY(caseId),
  );
  return JSON.parse(sessionValue || "false");
};

export const markReviewPopupAsDismissed = (caseId: string) => {
  sessionStorage.setItem(REVIEW_POPUP_DISMISSED_KEY(caseId), "true");
};
//endregion [[ Functions ]]

export const ReviewPopup = ({ ...props }: ReviewPopupProps) => {
  const [feedbackText, setFeedbackText] = useState(null);
  const [currentScore, setCurrentScore] = useState(0);
  const { exchanges, conversationStatus } = usePatient();

  const { showFeedbackPopup, setShowFeedbackPopup, caseId } = usePatient();

  const { translate } = useTranslate();

  const { clinic: clinicId, name: clinicName } = useClinicData();

  const handleClose = () => {
    evt({
      category: CATEGORY.USER_FEEDBACK,
      action: ACTION.PATIENT_SEND_FEEDBACK,
      props: {
        clinicName,
        clinicId,
        insertedText: feedbackText,
        score: null,
        totalNumberOfQuestions: exchanges.length,
        intakeEnded: conversationStatus,
        action: "dismiss",
      },
    });
    hidePopup();
  };

  const hidePopup = () => {
    markReviewPopupAsDismissed(caseId);
    setShowFeedbackPopup(false);
  };

  const handleSubmit = () => {
    evt({
      category: CATEGORY.USER_FEEDBACK,
      action: ACTION.PATIENT_SEND_FEEDBACK,
      props: {
        clinicName,
        clinicId,
        insertedText: feedbackText,
        score: currentScore,
        totalNumberOfQuestions: exchanges.length,
        intakeEnded: conversationStatus,
        action: "submit",
      },
    });
    hidePopup();
  };

  const handleScoreChanged = (score: number) => {
    evt({
      category: CATEGORY.USER_FEEDBACK,
      action: ACTION.PATIENT_SET_FEEDBACK_SCORE,
      props: {
        clinicName,
        clinicId,
        insertedText: feedbackText,
        score: currentScore,
        totalNumberOfQuestions: exchanges.length,
      },
    });
    setCurrentScore(score);
  };

  return (
    <ReviewPopupView open={showFeedbackPopup} onClose={handleClose}>
      <ThanksTitleArea>{translate("FEEDBACK_POPUP_TITLE")}</ThanksTitleArea>
      <SubtitleArea>{translate("FEEDBACK_POPUP_SUBTITLE")}</SubtitleArea>
      <StarRating onScoreChanged={handleScoreChanged} score={currentScore} />
      <TextAndButtonArea>
        <FeedbackDescription>
          {translate("FEEDBACK_POPUP_DESCRIPTION")}:
        </FeedbackDescription>
        <PatientTextField
          variant={"outlined"}
          onChange={(e) => setFeedbackText(e.target.value)}
          value={feedbackText || ""}
          rows={6}
          multiline
        />
        <GenericButton
          onClick={handleSubmit}
          label={translate("SUBMIT")}
          disabled={!feedbackText && !currentScore}
        />
      </TextAndButtonArea>
      <DismissButton onClick={handleClose}>
        {translate("DISMISS")}
      </DismissButton>
    </ReviewPopupView>
  );
};
